import { useState } from 'react'
import { Card, Row, Col, Badge, Button, Table, Modal, Form, Alert, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getDisplaySerieFolio } from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, formatDateTimeEsMX } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'
import { getFormaPagoSatLabel } from '../../constants/formaPagoSat'

const labelDocumento = (aplicacion) => {
  if (!aplicacion) return 'N/A'
  if (aplicacion.tipo_documento === 'bill' || aplicacion.bill_uuid || aplicacion.tipo === 'bill') {
    return (aplicacion.bill_uuid || aplicacion.label || '').toString().slice(0, 8) || 'N/A'
  }
  return getDisplaySerieFolio(aplicacion.factura_number || aplicacion.label || 'N/A')
}

const PaymentDetail = ({ payment, onEdit, onRefresh }) => {
  const queryClient = useQueryClient()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelMotivo, setCancelMotivo] = useState('')
  
  const cancelPaymentMutation = useMutation(
    async (motivo) => {
      return api.post(`/financial/payments/${payment.id}/cancelar/`, { motivo })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['payments'])
        queryClient.invalidateQueries(['payment-aplicaciones', payment.id])
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('bills')
        toast.success('Pago cancelado exitosamente')
        setShowCancelModal(false)
        setCancelMotivo('')
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Error al cancelar pago'
        toast.error(message)
      }
    }
  )
  
  const handleCancelPayment = () => {
    if (!cancelMotivo.trim()) {
      toast.error('Debe proporcionar un motivo para cancelar el pago')
      return
    }
    cancelPaymentMutation.mutate(cancelMotivo.trim())
  }

  const { data: aplicacionesApi, isLoading: loadingApps } = useQuery(
    ['payment-aplicaciones', payment.id],
    async () => {
      const response = await api.get(`/financial/aplicaciones-pago/?payment_id=${payment.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!payment.id }
  )

  const aplicacionesFromPayment = (payment.aplicaciones || []).map((a) => ({
    ...a,
    tipo_documento: a.tipo_documento || (a.bill || a.bill_uuid ? 'bill' : 'invoice'),
  }))

  const docsResumen = payment.documentos_aplicados || []

  const aplicaciones = (aplicacionesApi && aplicacionesApi.length > 0)
    ? aplicacionesApi
    : (aplicacionesFromPayment.length > 0 ? aplicacionesFromPayment : docsResumen.map((d) => ({
        id: `${d.tipo}-${d.id}`,
        tipo_documento: d.tipo === 'bill' ? 'bill' : 'invoice',
        factura_number: d.tipo === 'invoice' ? d.label : null,
        bill_uuid: d.tipo === 'bill' ? d.label : null,
        label: d.label,
        monto: d.monto,
        estado: 'aplicado',
        fecha_aplicacion: null,
        fecha_emision_documento: null,
      })))

  const aplicacionesActivas = aplicaciones.filter((a) => (a.estado || 'aplicado') === 'aplicado')

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'warning', text: 'Pendiente' },
      'aplicado': { bg: 'success', text: 'Aplicado' },
      'cancelado': { bg: 'danger', text: 'Cancelado' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      'ingreso': { bg: 'success', text: 'Ingreso' },
      'egreso': { bg: 'danger', text: 'Egreso' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>Pago #{payment.id}</h4>
          <div className="mt-2">
            {getTipoBadge(payment.tipo_pago)} {getEstadoBadge(payment.estado)}
          </div>
        </div>
        <div className="d-flex gap-2">
          {payment.estado !== 'cancelado' && (
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => setShowCancelModal(true)}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar Pago
            </Button>
          )}
          {payment.estado !== 'cancelado' && (
            <Button variant="outline-primary" size="sm" onClick={onEdit}>
              <i className="bi bi-pencil me-1"></i>
              Editar
            </Button>
          )}
        </div>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Cliente:</strong> {payment.cliente_name || 'N/A'}</p>
              <p><strong>Fecha:</strong> {formatDateOnlyEsMX(payment.payment_date, {}, '—')}</p>
              <p><strong>Forma de Pago:</strong> {payment.forma_pago_display || getFormaPagoSatLabel(payment.forma_pago) || payment.payment_method || 'N/A'}</p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Monto:</strong>
                <span className="ms-2">{formatCurrency(payment.amount || 0)}</span>
              </p>
              <p><strong>Referencia:</strong> {payment.reference || 'N/A'}</p>
              <p className="mb-1">
                <strong>
                  {payment.tipo_pago === 'egreso' ? 'Factura(s) recibida(s):' : 'Factura(s):'}
                </strong>
              </p>
              {loadingApps && aplicacionesActivas.length === 0 ? (
                <Spinner animation="border" size="sm" />
              ) : aplicacionesActivas.length > 0 ? (
                <div className="d-flex flex-wrap gap-1">
                  {aplicacionesActivas.map((apl) => (
                    <Badge
                      key={apl.id}
                      bg={apl.tipo_documento === 'bill' || apl.tipo === 'bill' ? 'secondary' : 'primary'}
                      className="fw-normal"
                    >
                      {apl.tipo_documento === 'bill' || apl.tipo === 'bill' ? 'Recibida' : 'Factura'}{' '}
                      {labelDocumento(apl)}
                      {apl.monto != null ? ` · ${formatCurrency(apl.monto || 0)}` : ''}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted">Sin factura aplicada</span>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="text-center">
                <h6 className="text-muted mb-1">Monto Total</h6>
                <h4 className="mb-0">{formatCurrency(payment.amount || 0)}</h4>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h6 className="text-muted mb-1">Monto Aplicado</h6>
                <h4 className="mb-0 text-success">{formatCurrency(payment.monto_aplicado || 0)}</h4>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h6 className="text-muted mb-1">Monto Disponible</h6>
                <h4 className="mb-0 text-info">{formatCurrency(payment.monto_disponible || 0)}</h4>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {aplicaciones && aplicaciones.length > 0 ? (
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              {payment.tipo_pago === 'egreso' ? 'Facturas Recibidas Aplicadas' : 'Facturas Aplicadas'} ({aplicaciones.length})
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>{payment.tipo_pago === 'egreso' ? 'Factura Recibida' : 'Factura'}</th>
                  <th>Fecha de Emisión</th>
                  <th>Fecha de Aplicación</th>
                  <th>Monto Aplicado</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {aplicaciones.map((aplicacion) => (
                  <tr key={aplicacion.id}>
                    <td>
                      <Badge
                        bg={aplicacion.tipo_documento === 'bill' || aplicacion.tipo === 'bill' ? 'secondary' : 'primary'}
                        className="me-2 fw-normal"
                      >
                        {aplicacion.tipo_documento === 'bill' || aplicacion.tipo === 'bill' ? 'Recibida' : 'Factura'}
                      </Badge>
                      <strong>{labelDocumento(aplicacion)}</strong>
                    </td>
                    <td>
                      {aplicacion.fecha_emision_documento
                        ? formatDateOnlyEsMX(aplicacion.fecha_emision_documento, {}, '—')
                        : '—'}
                    </td>
                    <td>
                      {aplicacion.fecha_aplicacion
                        ? formatDateTimeEsMX(aplicacion.fecha_aplicacion, undefined, '—')
                        : '—'}
                    </td>
                    <td>
                      <strong>{formatCurrency(aplicacion.monto || 0)}</strong>
                    </td>
                    <td>
                      <Badge bg={aplicacion.estado === 'aplicado' || !aplicacion.estado ? 'success' : aplicacion.estado === 'cancelado' ? 'danger' : 'warning'}>
                        {aplicacion.estado === 'aplicado' || !aplicacion.estado ? 'Aplicado' : aplicacion.estado === 'cancelado' ? 'Cancelado' : 'Pendiente'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3"><strong>Total Aplicado:</strong></td>
                  <td>
                    <strong>
                      {formatCurrency(aplicaciones.reduce((sum, app) => sum + parseFloat(app.monto || 0), 0))}
                    </strong>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="text-center py-4">
            {loadingApps ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <p className="text-muted mb-0">No hay aplicaciones de pago registradas</p>
                <small className="text-muted">Este pago aún no ha sido aplicado a ninguna factura</small>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancelar Pago #{payment.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Advertencia:</strong> Al cancelar este pago, se revertirán todas las aplicaciones activas 
            y los estados de las facturas afectadas se actualizarán.
          </Alert>
          
          {aplicacionesActivas.length > 0 && (
            <Alert variant="info" className="mt-2">
              <strong>Aplicaciones que se revertirán:</strong>
              <ul className="mb-0 mt-2">
                {aplicacionesActivas.map((app) => (
                  <li key={app.id}>
                    {app.tipo_documento === 'bill' || app.tipo === 'bill' ? 'Factura Recibida' : 'Factura'}{' '}
                    {labelDocumento(app)}: {formatCurrency(app.monto || 0)}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          
          <Form.Group className="mt-3">
            <Form.Label>Motivo de Cancelación *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancelMotivo}
              onChange={(e) => setCancelMotivo(e.target.value)}
              placeholder="Ingrese el motivo de cancelación..."
              required
            />
            <Form.Text className="text-muted">
              Este motivo será registrado en el historial de aplicaciones
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Cerrar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleCancelPayment}
            disabled={!cancelMotivo.trim() || cancelPaymentMutation.isLoading}
          >
            {cancelPaymentMutation.isLoading ? 'Cancelando...' : 'Confirmar Cancelación'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PaymentDetail
